import * as VW from "voximplant-websdk"
import { useAsyncFn } from "react-use"
import delay from "lodash.delay"

import { getLogger } from "modules/Call/VoximplantLogger"
import { OWNER } from "modules/Call/common/constants"
import { RequestLoginOneTimeKeyError, LoginError } from "modules/Call/common/utils/processError"
import { hasProp, isObject } from "utils/utils"
import { VOXIMPLANT_DOMAIN } from "utils/env-management"
import { httpClient } from "utils/infrastructure/httpClient"
import { VoximplantSdk } from "modules/Call/sdk"

export interface InitValues {
    loading: boolean
    ready: boolean
    error: Error | undefined
}

export interface InitHandlers {
    init(
        ref: HTMLElement,
        voximplantUserName: string,
        generateTokenApiUrl: string,
        owner: OWNER,
        bookingHash: string
    ): Promise<boolean>
}

async function initSdk(
    ref: HTMLElement,
    voximplantUserName: string,
    generateTokenApiUrl: string,
    owner: OWNER,
    bookingHash: string
) {
    if (!ref) throw new TypeError("Ref not initialized")
    const localVideoContainerId = ref.getAttribute("id")
    if (!localVideoContainerId) throw new TypeError("Attribute id not found")
    const sdk = VW.getInstance()
    const logger = getLogger({ bookingHash, owner, sdk })
    await navigator.mediaDevices.enumerateDevices()

    try {
        if (!sdk.alreadyInitialized) {
            logger.enable()

            await sdk.init({
                localVideoContainerId,
                rtcStatsCollectionInterval: 1000,
                showDebugInfo: true,
                node: VW.ConnectionNode.NODE_4,
            })
        }
        if (sdk.getClientState() === VW.ClientState.DISCONNECTED) {
            await sdk.connect()
        }
        if (sdk.getClientState() === VW.ClientState.CONNECTED) {
            const requestResult = await Promise.race([
                sdk.requestOneTimeLoginKey(`${voximplantUserName}@${VOXIMPLANT_DOMAIN}`),
                new Promise((resolve) => delay(resolve, 10000)).then(() => {
                    throw new RequestLoginOneTimeKeyError({})
                }),
            ])
            const { result, key, code } = requestResult
            if (result) return true
            if (code === VoximplantSdk.AuthResultCode.OneTimeKeyReceived) {
                const { token } = await httpClient.post<{ token: string }>(generateTokenApiUrl, {
                    one_time_key: key,
                })
                await sdk.loginWithOneTimeKey(`${voximplantUserName}@${VOXIMPLANT_DOMAIN}`, token)
            }
        }
        return true
    } catch (error) {
        if (isObject(error) && hasProp(error, "code")) {
            return Promise.reject(new LoginError(error.code, {}))
        }
        return Promise.reject(error)
    }
}

export function useInitSdk(): [InitValues, InitHandlers] {
    const [state, init] = useAsyncFn(initSdk, [])

    return [{ loading: state.loading, ready: !!state.value, error: state.error }, { init }]
}
